import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Box,
  Avatar,
  Button,
  CardMedia,
  Chip,
  Grid,
  Link,
  Typography
} from '@mui/material';
import { Pie } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { downloadFileFromS3 } from '../../api/aws/s3';
import noImage from '../../components/assets/noImage.svg';
import {
  black,
  blackText,
  blueBorderButton,
  blueButton,
  blueGrayText
} from '../../constants/Colors';
import { screenType } from '../../constants/enum';

import CustomGuideButton from './CustomGuideButton';

import type { ArticleType } from '../../api/microcms/article/articleType';

export interface GenderDataType {
  title: string;
  data: {
    labels: string[];
    datasets: [
      {
        label: string;
        data: number[];
        backgroundColor: string[];
        borderColor: string[];
      }
    ];
  };
  genderMaleButton?: boolean;
  genderFemaleButton?: boolean;
  handleMaleButton?: () => void;
  handleFemaleButton?: () => void;
}

interface Props {
  type: string;
  url?: boolean;
  itemName: string;
  image?: string;
  text?: string;
  arrayChips?: Array<{ value: number; option: string; checked?: boolean }>;
  tableRow?: boolean;
  tableItems?: Array<{
    title: string | null;
    text?: string | null;
    array?: Array<{ title: string; text?: string }>;
    hide?: boolean;
  }>;
  secondTableItems?: Array<{
    title: string;
    text?: string;
    array?: Array<{ title: string; text: string }>;
  }>;
  orderList?: string[];
  graphItems?: GenderDataType[];
  path?: string[];
  articleData?: ArticleType | null;
  articleImagePath?: string;
  screen?: number;
  guideText?: string;
  color?: string;
}

const styles = {
  articleBox: {
    '@media screen and (max-width:480px)': {
      flexDirection: 'column',
      padding: '8px'
    },
    columnGap: '16px',
    display: 'flex',
    flexDirection: 'row'
  },
  articleBoxWrapper: {
    '@media screen and (max-width:480px)': {
      padding: '0 16px 24px'
    },
    padding: '0 32px 24px 32px',
    width: '100%'
  },
  articleButton: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  articleDate: {
    '@media screen and (max-width:480px)': {
      mb: '6px'
    },
    color: blueGrayText,
    fontSize: '14px',
    mb: '8px'
  },
  articleImage: {
    '@media screen and (max-width:480px)': {
      width: '100%'
    },
    height: '140px',
    margin: { sm: '0 auto 0 0', xs: '0 auto' },
    width: '256px'
  },
  articleTexts: {
    display: 'flex',
    flexDirection: 'column'
  },
  articleTitle: {
    '@media screen and (max-width:480px)': {
      fontSize: '18px'
    },
    color: black,
    fontSize: '16px',
    fontWeight: 700
  },
  avatar: {
    height: '48px',
    width: '48px'
  },
  blueBorderButton,
  download: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px',
      marginLeft: '0',
      marginTop: '16px'
    },
    fontSize: '16px',
    textAlign: 'right'
  },
  fileText: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px',
      marginLeft: '0',
      marginTop: '16px'
    },
    fontSize: '16px',
    overflowWrap: 'break-word',
    textAlign: 'left',
    wordBreak: 'break-word'
  },
  genderButton: {
    ':focus': {
      bgcolor: '#FFF',
      border: '1px solid #0A7EBF',
      color: '#0A7EBF'
    },
    ':hover': {
      opacity: 0.8
    },
    fontSize: '13px',
    height: '30px',
    width: '70px'
  },
  genderFocusButton: {
    ':hover': {
      bgcolor: '#FFF',
      opacity: 0.8
    },
    bgcolor: '#FFF',
    border: '1px solid #0A7EBF',
    color: '#0A7EBF'
  },
  graph: {
    margin: '0 auto',
    pt: '24px'
  },
  graphBox: {
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    height: '300px',
    width: '324px'
  },
  graphBoxGender: {
    height: '350px'
  },
  graphBoxTitle: {
    bgcolor: '#F2F2F2',
    fontSize: '16px',
    height: '32px',
    textAlign: 'center'
  },
  graphs: {
    columnGap: '32px',
    justifyContent: {
      md: 'left',
      xs: 'center'
    },
    rowGap: '27px'
  },
  image: {
    height: '140px',
    margin: { sm: '0 auto 0 0', xs: '0 auto' },
    width: '256px'
  },
  imageAndText: {
    rowGap: '16px'
  },
  itemName: {
    color: black,
    fontSize: {
      md: '22px',
      xs: '20px'
    },
    fontWeight: '700',
    whiteSpace: 'pre-line',
    wordWrap: 'break-word'
  },
  tableItemText: {
    padding: {
      md: '14px 16px',
      xs: '10px 12px'
    }
  },
  tableItemTitle: {
    bgcolor: '#F2F2F2',
    padding: {
      md: '14px 16px',
      xs: '10px 12px'
    }
  },
  tableRow: {
    border: '1px solid #E0E0E0',
    fontSize: '12px'
  },
  tableText: {
    color: black,
    fontSize: {
      md: '14px',
      xs: '12px'
    }
  },
  tags: {
    columnGap: '8px',
    rowGap: '8px'
  },
  userInfo: {
    alignItems: 'center',
    columnGap: '16px',
    display: 'flex',
    flexDirection: 'row'
  },
  wrapper: {
    margin: {
      md: '40px 0',
      xs: '32px 0'
    },
    rowGap: '16px'
  }
};

const CustomShowDetail: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    type,
    url,
    itemName,
    image,
    text,
    arrayChips,
    tableRow,
    tableItems,
    secondTableItems,
    orderList,
    graphItems,
    path,
    articleData,
    articleImagePath,
    screen,
    guideText,
    color
  } = props;

  const downloadFile = (filePath: string[] | undefined): void => {
    void (async () => {
      if (filePath?.[0]) {
        await downloadFileFromS3(filePath[0]);
      }
    })();
  };

  const graphSizeChange = (item: GenderDataType): number => {
    if (
      item.title === t('media.gender') &&
      !item.genderMaleButton &&
      !item.genderFemaleButton
    ) {
      return 180;
    } else if (item.title === t('media.device')) {
      return 200;
    }
    return 220;
  };

  const formattedDate = (date: string | undefined): string | undefined => {
    if (date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const day = d.getDate();
      return `${year}/${month}/${day}`;
    }
  };

  return (
    <Grid
      container
      sx={
        color === 'red'
          ? {
              bgcolor: '#FFEBEB',
              margin: 0,
              padding: {
                md: '40px 0',
                xs: '32px 0'
              },
              rowGap: '16px'
            }
          : styles.wrapper
      }
    >
      <Grid item xs={12} md={4}>
        <Grid
          sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row' }}
        >
          <Typography component="p" sx={styles.itemName}>
            {itemName}
          </Typography>
          {guideText && (
            <CustomGuideButton title={itemName} guideText={guideText} />
          )}
        </Grid>
      </Grid>
      {type === 'iconAndName' && (
        <Grid item xs={12} md={8}>
          <Typography component="div" sx={styles.userInfo}>
            <Avatar sx={styles.avatar} src={image} />
            <Typography component="p" sx={blackText}>
              {text}
            </Typography>
          </Typography>
        </Grid>
      )}
      {type === 'image' && (
        <Grid item xs={12} md={8}>
          <Grid container sx={styles.imageAndText}>
            <Grid item xs={12}>
              <img
                src={image ?? noImage}
                style={{
                  height: 'auto',
                  objectFit: 'contain',
                  width: '300px'
                }}
                alt="mediaImage"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography component="p" sx={blackText}>
                {text}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
      {type === 'text' && (
        <Grid item xs={12} md={8}>
          <Typography component="p" sx={blackText}>
            {text}
          </Typography>
        </Grid>
      )}
      {type === 'url' && (
        <Grid item xs={12} md={8}>
          <Link href={text}>{text}</Link>
        </Grid>
      )}
      {type === 'tag' && (
        <Grid item xs={12} md={8}>
          <Grid container flexDirection="row" sx={styles.tags}>
            {arrayChips?.map((item) => (
              <Chip
                key={item.value}
                label={item.option}
                sx={{
                  backgroundColor: item.checked ? black : '#E0E0E0',
                  color: item.checked ? '#FFFFFF' : '#757575',
                  fontSize: '14px'
                }}
              />
            ))}
          </Grid>
        </Grid>
      )}
      {type === 'table' && (
        <Grid
          item
          xs={12}
          md={8}
          sx={tableRow ? { display: { md: 'flex', sx: 'block' } } : null}
        >
          <Grid
            sx={!tableRow && secondTableItems ? { mb: '24px' } : null}
            {...(tableRow ? { item: true, xs: 12 } : null)}
          >
            {tableItems?.map(
              (item, index) =>
                !item.hide &&
                item.title && (
                  <Grid container key={index} sx={styles.tableRow}>
                    <Grid
                      item
                      xs={6}
                      {...(tableRow ? { md: 8 } : { md: 4 })}
                      sx={styles.tableItemTitle}
                    >
                      <Typography
                        component="p"
                        sx={[styles.tableText, { fontWeight: 'bold' }]}
                      >
                        {item.title ?? ''}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      {...(tableRow ? { md: 4 } : { md: 8 })}
                      sx={styles.tableItemText}
                    >
                      {item.text && !url && (
                        <Typography component="p" sx={styles.tableText}>
                          {item.text ?? ''}
                        </Typography>
                      )}
                      {item.text && url && (
                        <Link href={item.text}>{item.text}</Link>
                      )}
                      {item.array?.map((mdallItem, mdallItemIndex) => (
                        <Typography
                          component="p"
                          sx={styles.tableText}
                          key={mdallItemIndex}
                        >
                          {mdallItem.title}
                          {mdallItem.text && '：'}
                          {mdallItem.text}
                        </Typography>
                      ))}
                    </Grid>
                  </Grid>
                )
            )}
          </Grid>
          <Grid {...(tableRow ? { item: true, xs: 12 } : null)}>
            {secondTableItems?.map((item, index) => (
              <Grid container key={index} sx={styles.tableRow}>
                <Grid
                  item
                  xs={6}
                  {...(tableRow ? { md: 8 } : { md: 4 })}
                  sx={styles.tableItemTitle}
                >
                  <Typography
                    component="p"
                    sx={[styles.tableText, { fontWeight: 'bold' }]}
                  >
                    {item.title}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  {...(tableRow ? { md: 4 } : { md: 8 })}
                  sx={styles.tableItemText}
                >
                  {item.text && (
                    <Typography component="p" sx={styles.tableText}>
                      {item.text ?? ''}
                    </Typography>
                  )}
                  {item.array?.map((arrayItem, arrayItemIndex) => (
                    <Typography component="p" key={arrayItemIndex}>
                      {arrayItem.title}:{arrayItem.text}
                    </Typography>
                  ))}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      {type === 'list' && (
        <Grid item xs={12} md={8} sx={{ display: { md: 'flex', sx: 'block' } }}>
          <Grid item xs={12}>
            {tableItems?.map((item, index) => (
              <>
                {item.title && (
                  <Grid container key={index} sx={styles.tableRow}>
                    <Grid item xs={12} sx={styles.tableItemText}>
                      {item.title && !url && (
                        <Typography component="p" sx={styles.tableText}>
                          {item.title}
                        </Typography>
                      )}
                      {item.title && url && (
                        <Link href={item.title}>{item.title}</Link>
                      )}
                    </Grid>
                  </Grid>
                )}
              </>
            ))}
          </Grid>
        </Grid>
      )}
      {type === 'orderList' && (
        <Grid item xs={12} md={8}>
          {orderList?.map((item, index) => (
            <Grid container key={index} sx={styles.tableRow}>
              <Grid item sx={[styles.tableItemTitle, { width: '42px' }]}>
                <Typography component="p">{index + 1}</Typography>
              </Grid>
              <Grid item sx={[styles.tableItemText, { flex: 1 }]}>
                <Typography component="p">{item}</Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}
      {type === 'graph' && (
        <Grid item xs={12} md={8}>
          <Grid container sx={styles.graphs}>
            {graphItems?.map((item, index) => (
              <Grid
                item
                key={index}
                sx={{
                  ...styles.graphBox,
                  ...(item.genderMaleButton && styles.graphBoxGender),
                  ...(item.genderFemaleButton && styles.graphBoxGender)
                }}
              >
                <Typography
                  component="div"
                  sx={[styles.graphBoxTitle, blackText]}
                >
                  {item.title}
                </Typography>
                <Grid sx={styles.graph}>
                  <Pie
                    key={`${item.genderMaleButton === true ? 'male' : ''}-${
                      item.genderFemaleButton === true ? 'female' : ''
                    }`}
                    data={item.data}
                    width={graphSizeChange(item)}
                    height={graphSizeChange(item)}
                    options={{
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                          labels: {
                            boxHeight: 10,
                            boxWidth: 10
                          },
                          position: 'bottom'
                        }
                      }
                    }}
                  />
                </Grid>
                {item.genderMaleButton !== undefined && (
                  <>
                    <Grid
                      container
                      columnGap="8px"
                      justifyContent="center"
                      mt="8px"
                    >
                      <Button
                        sx={{
                          ...blueButton,
                          ...styles.genderButton,
                          ...(item.genderMaleButton && styles.genderFocusButton)
                        }}
                        onClick={item.handleMaleButton}
                        onMouseDown={(
                          event: React.MouseEvent<HTMLButtonElement>
                        ) => {
                          event.preventDefault();
                        }}
                      >
                        {t('media.maleDetail')}
                      </Button>
                      <Button
                        sx={{
                          ...blueButton,
                          ...styles.genderButton,
                          ...(item.genderFemaleButton &&
                            styles.genderFocusButton)
                        }}
                        onClick={item.handleFemaleButton}
                        onMouseDown={(
                          event: React.MouseEvent<HTMLButtonElement>
                        ) => {
                          event.preventDefault();
                        }}
                      >
                        {t('media.femaleDetail')}
                      </Button>
                    </Grid>
                    <Typography
                      sx={{
                        color: black,
                        fontSize: '8px',
                        mt: '3px',
                        textAlign: 'center'
                      }}
                    >
                      {t('media.followText')}
                    </Typography>
                  </>
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      {type === 'file' && (
        <Grid item xs={12} md={8}>
          <Typography sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={[styles.fileText]}>{text}</Typography>
            <Typography sx={[styles.download, { minWidth: '142px' }]}>
              <Button
                sx={styles.blueBorderButton}
                onClick={() => downloadFile(path)}
              >
                <FileDownloadOutlinedIcon />
                <Typography>{t('orientation.download')}</Typography>
              </Button>
            </Typography>
          </Typography>
        </Grid>
      )}
      {type === 'media' && articleData !== null && (
        <Grid item>
          <Box sx={styles.articleBox}>
            <CardMedia
              component="img"
              image={articleImagePath !== '' ? articleImagePath : noImage}
              sx={styles.articleImage}
            />
            <Grid sx={styles.articleTexts}>
              <Typography component="div" sx={styles.articleDate}>
                {`${t('mypage.articleList.createdDate')}：${
                  formattedDate(articleData?.createdAt) ?? ''
                }`}
              </Typography>
              <Typography component="div" sx={styles.articleTitle}>
                {articleData?.title}
              </Typography>
            </Grid>
          </Box>
          <Box sx={styles.articleButton}>
            <Button
              color="error"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                if (screen === screenType.client) {
                  navigate(`/client/mypage/article/${articleData?.id ?? ''}`);
                } else if (screen === screenType.agent) {
                  navigate(`/agent/mypage/article/${articleData?.id ?? ''}`);
                }
              }}
            >
              {t('mediaArticle.show')}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default CustomShowDetail;
